import { clsx } from "clsx";
import { type FC, forwardRef, memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type ItemContent, type ListProps, Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import { titleCase } from "title-case";
import { ActionButtonIntent, AlertDialogComponent, Popover, toTimeAgo, TriggerButton } from "widgets";
import { useOverlay } from "widgets/hooks";

import { type Project, type ProjectActivity as ProjectActivityGql, type User } from "../../graphql.js";
import { deleteActivities } from "@/redux/modules/project.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { ProjectActivity } from "./ProjectActivity.js";
import stylesBody from "./ProjectActivityBody.module.css";
import { filterProjectActivities } from "./ProjectActivitySearchEngine.js";
import styles from "./ProjectActivityStream.module.css";

export interface ProjectActivityStreamProps {
  activities: ProjectActivityGql[];
  filterQuery?: string;

  scrollToActivityId?: string | null;
}

const NO_MATCH_FOUND_MSG = "No items meet the search criteria";
const NO_ACTIVITY_MSG = "No activities found";
const LATEST_ACTIVITY_GROUP_KEY = "Today";
const YESTERDAY_ACTIVITY_GROUP_KEY = "Yesterday";

const toActivityGroupKey = (created: string): string => {
  const rawLabel = toTimeAgo(created, true);
  const trimmedLabel = rawLabel.trim();

  if (!trimmedLabel) {
    return LATEST_ACTIVITY_GROUP_KEY;
  }

  const lowercaseLabel = trimmedLabel.toLowerCase();
  if (lowercaseLabel === "today") {
    return LATEST_ACTIVITY_GROUP_KEY;
  }

  if (lowercaseLabel === "yesterday") {
    return YESTERDAY_ACTIVITY_GROUP_KEY;
  }

  return trimmedLabel.charAt(0).toUpperCase() + trimmedLabel.slice(1);
};

type VirtualizedItem =
  | {
      kind: "header";
      id: string;
      title: string;
      groupKey: string;
    }
  | {
      kind: "activity";
      id: string;
      activity: ProjectActivityGql;
      groupKey: string;
    };

const ConversationList = forwardRef<HTMLDivElement, ListProps>((props, ref) => {
  return <div {...props} ref={ref} className={styles.list} role="list" />;
});
ConversationList.displayName = "ConversationList";

export const ProjectActivityStream: FC<ProjectActivityStreamProps> = memo(function ProjectActivityStream(props) {
  const { activities, filterQuery, scrollToActivityId } = props;

  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const project = useSelector<IAppState, Project>((state) => state.project.project);

  const projectMembers: User[] = useMemo(
    () => project?.projectMembers?.map((x) => x.user).filter((x) => x != null) ?? [],
    [project],
  );
  const [highlightedActivity, setHighlightedActivity] = useState<{ id: string; token: number } | null>(null);
  const deferredFilterQuery = useDeferredValue(filterQuery);

  const filteredActivities = useMemo(() => {
    if (!activities?.length) {
      return [];
    }
    return filterProjectActivities(activities, deferredFilterQuery ?? "");
  }, [activities, deferredFilterQuery]);

  const groupedActivities = useMemo(() => {
    if (!filteredActivities.length) {
      return {};
    }
    const grouped = filteredActivities.reduce<Record<string, ProjectActivityGql[]>>((acc, activity) => {
      const groupKey = toActivityGroupKey(activity.created);
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(activity);
      return acc;
    }, {});

    if (!Object.prototype.hasOwnProperty.call(grouped, LATEST_ACTIVITY_GROUP_KEY)) {
      grouped[LATEST_ACTIVITY_GROUP_KEY] = [];
    }

    return grouped;
  }, [filteredActivities]);

  const flattenedData = useMemo(() => {
    const groupKeys = Object.keys(groupedActivities);
    if (!groupKeys.length) {
      return {
        items: [] as VirtualizedItem[],
      };
    }

    const orderedGroupKeys = groupKeys.slice().reverse();
    const items: VirtualizedItem[] = [];

    orderedGroupKeys.forEach((groupKey) => {
      const groupActivities = groupedActivities[groupKey] ?? [];
      if (!groupActivities.length) {
        return;
      }
      const chronologicalActivities = groupActivities.slice().reverse();
      items.push({
        kind: "header",
        id: `header-${groupKey}`,
        title: groupKey,
        groupKey,
      });

      chronologicalActivities.forEach((activity) => {
        items.push({
          kind: "activity",
          id: String(activity.id),
          activity,
          groupKey,
        });
      });
    });

    return {
      items,
    };
  }, [groupedActivities]);

  const { items } = flattenedData;
  const activityIndexLookup = useMemo(() => {
    const lookup = new Map<string, number>();
    items.forEach((item, index) => {
      if (item.kind === "activity") {
        lookup.set(item.id, index);
      }
    });
    return lookup;
  }, [items]);

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const lastScrolledActivityIdRef = useRef<string | null>(null);
  const initialScrollToLatestRef = useRef(false);

  const handleAtBottomChange = useCallback((atBottom: boolean) => {
    if (atBottom) {
      lastScrolledActivityIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!scrollToActivityId) {
      return;
    }
    const targetId = String(scrollToActivityId);
    const targetIndex = activityIndexLookup.get(targetId);
    if (targetIndex == null) {
      return;
    }
    const targetItem = items[targetIndex];
    if (targetItem == null || targetItem.kind !== "activity") {
      return;
    }
    if (lastScrolledActivityIdRef.current === targetId) {
      return;
    }

    lastScrolledActivityIdRef.current = targetId;
    virtuosoRef.current?.scrollToIndex({ index: targetIndex, align: "center", behavior: "auto" });
    setHighlightedActivity({ id: targetId, token: Date.now() });
  }, [activityIndexLookup, items, scrollToActivityId]);

  useEffect(() => {
    if (!scrollToActivityId) {
      lastScrolledActivityIdRef.current = null;
    }
  }, [scrollToActivityId]);

  useEffect(() => {
    if (!items.length) {
      initialScrollToLatestRef.current = false;
      return;
    }
    if (scrollToActivityId) {
      return;
    }
    if (initialScrollToLatestRef.current) {
      return;
    }

    virtuosoRef.current?.scrollToIndex({ index: "LAST", align: "end", behavior: "auto" });
    initialScrollToLatestRef.current = true;
  }, [items, scrollToActivityId]);

  const computeItemKey = useCallback(
    (_index: number, item: VirtualizedItem) => {
      if (item.kind === "header") {
        return item.id;
      }
      const baseKey = `activity-${item.id}`;
      if (highlightedActivity?.id === item.id) {
        return `${baseKey}-${highlightedActivity.token}`;
      }
      return baseKey;
    },
    [highlightedActivity],
  );

  const getItemContent = useCallback<ItemContent<VirtualizedItem, unknown>>(
    (_, item) => {
      if (item?.kind === "header") {
        return (
          <div className={styles.groupHeader} role="heading" aria-level={3} key={`${item.id}-${item.kind}`}>
            <span>{item.title}</span>
          </div>
        );
      }

      if (!item) {
        return null;
      }

      const isScrollTarget = scrollToActivityId != null && item.id === String(scrollToActivityId);
      const activityKey = isScrollTarget ? "pa-scroll" : `pa-${item.id}`;
      const isHighlighted = highlightedActivity?.id === item.id;
      const highlightKeySuffix = isHighlighted ? `highlight-${highlightedActivity?.token}` : "normal";

      return (
        <div
          key={`${activityKey}-${highlightKeySuffix}`}
          className={clsx(styles.itemWrapper, {
            [styles.flashWrapperHighlight]: isHighlighted,
          })}
        >
          <ProjectActivity
            activity={item.activity}
            currentUser={currentUser}
            users={projectMembers}
            action={item.activity.deletable && <DeletePopover activity={item.activity} />}
          />
        </div>
      );
    },
    [currentUser, highlightedActivity, projectMembers, scrollToActivityId],
  );

  useEffect(() => {
    if (!highlightedActivity) {
      return;
    }
    const timer = window.setTimeout(() => setHighlightedActivity(null), 3500);
    return () => window.clearTimeout(timer);
  }, [highlightedActivity]);

  if (!items.length) {
    if (filterQuery?.trim()) {
      return <ContentPlaceholder key="noMatchFound">{NO_MATCH_FOUND_MSG}</ContentPlaceholder>;
    }
    return <ContentPlaceholder key="noActivityFound">{NO_ACTIVITY_MSG}</ContentPlaceholder>;
  }

  return (
    <Virtuoso
      ref={virtuosoRef}
      role="list"
      data={items}
      components={{
        List: ConversationList,
        Footer: FakeFooter, // force the bottom margin
      }}
      computeItemKey={computeItemKey}
      itemContent={getItemContent}
      atBottomThreshold={100}
      atBottomStateChange={handleAtBottomChange}
      followOutput={(isAtBottom) => isAtBottom}
    />
  );
});

const FakeFooter = () => <div style={{ height: 0, lineHeight: 0 }}>&nbsp;</div>;

const DeletePopover = ({ activity }: { activity: ProjectActivityGql }) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay();
  const dispatch = useDispatch<ReduxDispatch>();
  const activityType = activity.type.toLowerCase();

  return (
    <>
      <TriggerButton
        ref={triggerRef}
        {...triggerProps}
        className={stylesBody.deleteButton}
        icon={"delete"}
        label={titleCase(`Delete ${activityType}`)}
        onPress={state.open}
      />
      <Popover {...overlayProps} state={state} triggerRef={triggerRef} placement="top" offset={10}>
        <AlertDialogComponent
          closeFn={state.close}
          title={titleCase(`Delete ${activityType}?`)}
          mainButton={{
            label: "Delete",
            intent: ActionButtonIntent.Danger,
            onPress: () => dispatch(deleteActivities([activity.id], activityType)).then(state.close),
          }}
        >
          Are you sure you want to delete this {activityType}?
        </AlertDialogComponent>
      </Popover>
    </>
  );
};
